import React, { createContext, useContext, useState, ReactNode, useMemo, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

export interface BreadcrumbItem {
  name: string;
  href: string;
  current: boolean;
  /**
   * Rendered in place of `name`, when a crumb needs to be more than text —
   * a title that animates as it changes, say. `name` is still required: it
   * is what hover, assistive tech and the document title read.
   */
  node?: ReactNode;
}

interface BreadcrumbsContextValue {
  breadcrumbs: BreadcrumbItem[];
  setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void;
}

const BreadcrumbsContext = createContext<BreadcrumbsContextValue | undefined>(undefined);

export const BreadcrumbsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const location = useLocation();

  // Auto-reset breadcrumbs on route change.
  //
  // A LAYOUT effect on purpose: React runs a parent's passive effects after
  // its children's, so a plain useEffect here cleared the crumbs a page had
  // just set for the new route - every in-app navigation between two pages
  // that set crumbs lost them. Layout effects all run before any passive
  // effect, so the reset lands first and the page's own crumbs win.
  useLayoutEffect(() => {
    setBreadcrumbs([]);
  }, [location.pathname]);

  // Memoize context value - keep setBreadcrumbs reference stable
  const value = useMemo(
    () => ({ breadcrumbs, setBreadcrumbs }),
    [breadcrumbs, setBreadcrumbs]
  );

  return (
    <BreadcrumbsContext.Provider value={value}>
      {children}
    </BreadcrumbsContext.Provider>
  );
};

export const useBreadcrumbs = () => {
  const context = useContext(BreadcrumbsContext);
  if (!context) {
    throw new Error('useBreadcrumbs must be used within a BreadcrumbsProvider');
  }
  return context;
};

export const useOptionalBreadcrumbs = () => {
  return useContext(BreadcrumbsContext);
};
